import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaBolt,
  FaCheck,
  FaCopy,
  FaFlag,
  FaGamepad,
  FaHandPaper,
  FaLayerGroup,
  FaLink,
  FaMoneyBillWave,
  FaPlay,
  FaShieldAlt,
  FaSignOutAlt,
  FaUsers,
} from "react-icons/fa";
import {
  createBlackjackSession,
  getGameSession,
  getGameSessionPlayers,
  getMyOpenGameSession,
  joinBlackjackSession,
  joinPublicBlackjackSession,
  leaveGameSession,
  leaveBlackjackSession,
  placeBlackjackBet,
  sendBlackjackReaction,
  setBlackjackPlayerReady,
  sitOutBlackjackHand,
  resumeBlackjackBetting,
  subscribeToGamePresence,
  startBlackjackSession,
  submitBlackjackAction,
  submitBlackjackInsurance,
  tickBlackjackSession,
  subscribeToGameSession,
} from "../../../services/games/gameService";
import GameRoomBackButton from "../GameRoomBackButton";
import "./OnlineBlackjack.css";

const DEFAULT_BET = 25;
const BET_STEP = 25;
const MAX_SPLIT_HANDS = 4;

const REACTIONS = [
  { key: "nice_hit", emoji: "🔥", label: "Nice Hit!" },
  { key: "great_stand", emoji: "👏", label: "Great Stand" },
  { key: "close_one", emoji: "😮", label: "Close One" },
  { key: "twenty_one", emoji: "🎯", label: "21!" },
  { key: "doh", emoji: "😂", label: "Doh!" },
  { key: "ouch", emoji: "😬", label: "Ouch" },
  { key: "lucky", emoji: "🍀", label: "Lucky!" },
  { key: "bust", emoji: "💀", label: "Bust!" },
];

const REACTION_MAP = Object.fromEntries(
  REACTIONS.map((reaction) => [reaction.key, reaction])
);

function money(value) {
  const amount = Number(value || 0);
  return amount.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function formatRefillCountdown(iso) {
  if (!iso) return null;
  const ms = Math.max(0, new Date(iso).getTime() - Date.now());
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes}m`;
  return `${hours}h ${minutes}m`;
}

function rankValue(rank) {
  if (rank === "A") return 11;
  if (["K", "Q", "J"].includes(rank)) return 10;
  return Number(rank || 0);
}

function cardTotal(cards = []) {
  let total = 0;
  let aces = 0;

  cards.forEach((card) => {
    total += rankValue(card.rank);
    if (card.rank === "A") aces += 1;
  });

  while (total > 21 && aces > 0) {
    total -= 10;
    aces -= 1;
  }

  return total;
}

function canSplitCards(cards = []) {
  return (
    cards.length === 2 &&
    rankValue(cards[0]?.rank) === rankValue(cards[1]?.rank)
  );
}

function playerHands(playerState) {
  if (Array.isArray(playerState?.hands) && playerState.hands.length) {
    return playerState.hands;
  }

  if (Array.isArray(playerState?.cards) && playerState.cards.length) {
    return [
      {
        cards: playerState.cards,
        bet: playerState.bet || 0,
        status: playerState.status || "playing",
        from_split: false,
        doubled: !!playerState.doubled,
        surrendered: !!playerState.surrendered,
        result: playerState.result || null,
        payout: playerState.payout || 0,
      },
    ];
  }

  return [];
}

function statusLabel(status) {
  switch (status) {
    case "betting":
      return "BET NOW";
    case "bet":
      return "BET LOCKED";
    case "sitting_out":
      return "SITTING OUT";
    case "auto_sit_out":
      return "SITTING OUT";
    case "out_of_chips":
      return "OUT OF CHIPS";
    case "insurance_pending":
      return "INSURANCE?";
    case "playing":
      return "PLAYING";
    case "stood":
      return "STOOD";
    case "complete":
      return "COMPLETE";
    default:
      return status ? status.replaceAll("_", " ").toUpperCase() : "WAITING";
  }
}

function PlayingCard({ card, hidden = false, compact = false }) {
  if (hidden) {
    return (
      <div className={`online-bj-card is-hidden ${compact ? "is-compact" : ""}`}>
        <div />
      </div>
    );
  }

  if (!card) return null;

  const red = card.suit === "♥" || card.suit === "♦";

  return (
    <div className={`online-bj-card ${red ? "is-red" : ""} ${compact ? "is-compact" : ""}`}>
      <div className="online-bj-card__corner">
        <strong>{card.rank}</strong>
        <span>{card.suit}</span>
      </div>
      <div className="online-bj-card__suit">{card.suit}</div>
    </div>
  );
}

export default function OnlineBlackjack({ currentUser }) {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [players, setPlayers] = useState([]);
  const [joinCodeInput, setJoinCodeInput] = useState("");
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [bet, setBet] = useState(DEFAULT_BET);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [reactionWorking, setReactionWorking] = useState(false);
  const [seatReactions, setSeatReactions] = useState({});
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());
  const [activityFeed, setActivityFeed] = useState([]);
  const deadlineTickInFlightRef = useRef(false);
  const lastDeadlineAttemptAtRef = useRef(0);
  const reactionTimersRef = useRef({});
  const seenReactionIdsRef = useRef(new Set());
  const lastActivityMessageRef = useRef("");

  const activePlayers = useMemo(
    () => players.filter((player) => !player.left_at),
    [players]
  );

  const myPlayer = activePlayers.find(
    (player) => player.user_id === currentUser?.id
  );

  const host = session?.host_user_id === currentUser?.id;
  const state = session?.game_state || {};
  const statePlayers = state.players || {};
  const myState = myPlayer ? statePlayers[String(myPlayer.seat)] : null;
  const myHands = playerHands(myState);
  const myActiveHandIndex = Number(myState?.active_hand || 0);
  const myActiveHand = myHands[myActiveHandIndex] || null;
  const myBankroll = Number(myState?.bankroll || 0);
  const myMaxWager = Math.max(0, Math.floor(myBankroll / BET_STEP) * BET_STEP);
  const myRoundStake =
    myHands.reduce((sum, hand) => sum + Number(hand.bet || 0), 0) +
    Number(myState?.insurance_bet || 0);
  const myRoundReturn = Number(myState?.payout || 0);
  const myRoundNet = myRoundReturn - myRoundStake;

  const myTurn = Boolean(
    session?.status === "active" &&
      session?.current_player_id === currentUser?.id &&
      state.phase === "player"
  );

  const canDouble = Boolean(
    myTurn &&
      myActiveHand?.status === "playing" &&
      myActiveHand?.cards?.length === 2 &&
      Number(myState?.bankroll || 0) >= Number(myActiveHand?.bet || 0)
  );

  const canSurrender = Boolean(
    myTurn &&
      myHands.length === 1 &&
      myActiveHand?.status === "playing" &&
      !myActiveHand?.from_split &&
      myActiveHand?.cards?.length === 2
  );

  const canSplit = Boolean(
    myTurn &&
      myActiveHand?.status === "playing" &&
      canSplitCards(myActiveHand?.cards || []) &&
      myHands.length < MAX_SPLIT_HANDS &&
      Number(myState?.bankroll || 0) >= Number(myActiveHand?.bet || 0)
  );

  const activeDeadline = useMemo(() => {
    if (state.phase === "betting") return state.betting_deadline || null;
    if (state.phase === "insurance") return state.insurance_deadline || null;
    if (state.phase === "player") return state.turn_deadline || null;
    if (state.phase === "complete") return state.round_deadline || null;
    return null;
  }, [
    state.phase,
    state.betting_deadline,
    state.insurance_deadline,
    state.turn_deadline,
    state.round_deadline,
  ]);

  const refreshTable = useCallback(async (sessionId) => {
    if (!sessionId) return;

    try {
      const [{ session: nextSession }, nextPlayers] = await Promise.all([
        getGameSession(sessionId),
        getGameSessionPlayers(sessionId),
      ]);

      setSession(nextSession);
      setPlayers(nextPlayers || []);
    } catch (err) {
      console.error("Online Blackjack refresh failed:", err);
      setMessage(err.message || "Could not refresh the blackjack table.");
    }
  }, []);

  const showReaction = useCallback((reaction) => {
    const seat = Number(reaction?.seat || 0);
    const key = reaction?.reaction_key;
    const definition = REACTION_MAP[key];

    if (!seat || !definition) return;

    const reactionId = reaction?.id != null ? String(reaction.id) : null;
    if (reactionId && seenReactionIdsRef.current.has(reactionId)) return;

    if (reactionId) {
      seenReactionIdsRef.current.add(reactionId);
      if (seenReactionIdsRef.current.size > 100) {
        seenReactionIdsRef.current = new Set(
          Array.from(seenReactionIdsRef.current).slice(-50)
        );
      }
    }

    setSeatReactions((current) => ({
      ...current,
      [seat]: {
        id: reactionId || `${seat}-${Date.now()}`,
        ...definition,
      },
    }));

    if (reactionTimersRef.current[seat]) {
      window.clearTimeout(reactionTimersRef.current[seat]);
    }

    reactionTimersRef.current[seat] = window.setTimeout(() => {
      setSeatReactions((current) => {
        if (!current[seat]) return current;
        const next = { ...current };
        delete next[seat];
        return next;
      });
      delete reactionTimersRef.current[seat];
    }, 3400);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(reactionTimersRef.current).forEach((timer) =>
        window.clearTimeout(timer)
      );
      reactionTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    const text = String(state.message || "").trim();
    if (!text || text === lastActivityMessageRef.current) return;

    lastActivityMessageRef.current = text;
    const entry = {
      id: `${session?.version || 0}-${Date.now()}`,
      text,
      time: new Date().toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      }),
    };

    setActivityFeed((current) => [entry, ...current].slice(0, 5));
  }, [state.message, session?.version]);

  useEffect(() => {
    let cancelled = false;

    const recover = async () => {
      if (!currentUser?.id || session?.id) return;

      try {
        const id = await getMyOpenGameSession("blackjack");
        if (!cancelled && id) await refreshTable(id);
      } catch (err) {
        console.warn("Online Blackjack reconnect lookup failed:", err);
      }
    };

    recover();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, session?.id, refreshTable]);

  useEffect(() => {
    if (!session?.id || !currentUser?.id) {
      setOnlineUserIds(new Set());
      return undefined;
    }

    return subscribeToGamePresence({
      sessionId: session.id,
      userId: currentUser.id,
      onSync: (userIds) => setOnlineUserIds(new Set(userIds)),
    });
  }, [session?.id, currentUser?.id]);

  useEffect(() => {
    if (!session?.id) return undefined;

    const unsubscribe = subscribeToGameSession(session.id, {
      onSessionChange: (payload) => {
        if (payload?.new?.id === session.id) setSession(payload.new);
      },
      onPlayersChange: () => refreshTable(session.id),
      onAction: () => refreshTable(session.id),
      onReaction: (payload) => {
        if (payload?.new) showReaction(payload.new);
      },
    });

    return unsubscribe;
  }, [session?.id, refreshTable, showReaction]);

  useEffect(() => {
    if (!session?.id || !activeDeadline || session.status !== "active") {
      setSecondsLeft(null);
      deadlineTickInFlightRef.current = false;
      lastDeadlineAttemptAtRef.current = 0;
      return undefined;
    }

    let cancelled = false;

    const tickClock = async () => {
      const deadlineMs = new Date(activeDeadline).getTime();
      const ms = deadlineMs - Date.now();
      const nextSeconds = Math.max(0, Math.ceil(ms / 1000));

      if (!cancelled) setSecondsLeft(nextSeconds);

      if (ms > 0) return;

      // Once the browser reaches zero, keep asking the idempotent server tick
      // until the authoritative phase/deadline actually changes. The previous
      // one-shot guard could strand the UI forever if a refresh/realtime event
      // was missed after the server advanced.
      const nowMs = Date.now();
      if (deadlineTickInFlightRef.current) return;
      if (nowMs - lastDeadlineAttemptAtRef.current < 1000) return;

      deadlineTickInFlightRef.current = true;
      lastDeadlineAttemptAtRef.current = nowMs;

      try {
        await tickBlackjackSession(session.id);

        if (!cancelled) {
          await refreshTable(session.id);
        }
      } catch (err) {
        const text = String(err?.message || "");
        if (!text.includes("ROUND_RESULT_DISPLAY_ACTIVE")) {
          console.warn("Blackjack deadline advance failed:", err);
        }
      } finally {
        deadlineTickInFlightRef.current = false;
      }
    };

    tickClock();
    const timer = window.setInterval(tickClock, 250);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      deadlineTickInFlightRef.current = false;
    };
  }, [session?.id, session?.status, state.phase, activeDeadline, refreshTable]);

  const createTable = async (visibility) => {
    if (working) return;

    setWorking(true);
    setMessage("");

    try {
      const created = await createBlackjackSession({
        maxPlayers: 4,
        visibility,
      });
      await refreshTable(created.session_id);
    } catch (err) {
      setMessage(err.message || "Could not create a blackjack table.");
    } finally {
      setWorking(false);
    }
  };

  const joinPrivate = async (event) => {
    event?.preventDefault?.();
    if (!joinCodeInput.trim() || working) return;

    setWorking(true);
    setMessage("");

    try {
      const joined = await joinBlackjackSession(joinCodeInput);
      await refreshTable(joined.session_id);
    } catch (err) {
      setMessage(err.message || "Could not join that table.");
    } finally {
      setWorking(false);
    }
  };

  const joinPublic = async () => {
    if (working) return;

    setWorking(true);
    setMessage("");

    try {
      const joined = await joinPublicBlackjackSession();
      await refreshTable(joined.session_id);
    } catch (err) {
      setMessage(err.message || "Could not join a public table.");
    } finally {
      setWorking(false);
    }
  };

  const toggleReady = async () => {
    if (!session?.id || !myPlayer || working) return;

    setWorking(true);
    setMessage("");

    try {
      await setBlackjackPlayerReady(session.id, !myPlayer.ready);
      await refreshTable(session.id);
    } catch (err) {
      setMessage(err.message || "Could not update ready state.");
    } finally {
      setWorking(false);
    }
  };

  const startTable = async () => {
    if (!session?.id || !host || working) return;

    setWorking(true);
    setMessage("");

    try {
      await startBlackjackSession(session.id);
      await refreshTable(session.id);
    } catch (err) {
      setMessage(err.message || "Could not start the table.");
    } finally {
      setWorking(false);
    }
  };

  const placeBet = async () => {
    if (!session?.id || !myState || working) return;

    setWorking(true);
    setMessage("");

    try {
      await placeBlackjackBet({
        sessionId: session.id,
        amount: Math.min(bet, myMaxWager),
        expectedVersion: session.version,
      });
      await refreshTable(session.id);
    } catch (err) {
      setMessage(
        String(err.message || "").includes("STALE_GAME_STATE")
          ? "The table changed. Refreshing…"
          : err.message || "Bet was not accepted."
      );
      await refreshTable(session.id);
    } finally {
      setWorking(false);
    }
  };

  const insuranceDecision = async (take) => {
    if (!session?.id || !myState || working) return;

    setWorking(true);
    setMessage("");

    try {
      await submitBlackjackInsurance({
        sessionId: session.id,
        take,
        expectedVersion: session.version,
      });
      await refreshTable(session.id);
    } catch (err) {
      setMessage(
        String(err.message || "").includes("STALE_GAME_STATE")
          ? "The table changed. Refreshing…"
          : err.message || "Insurance decision was not accepted."
      );
      await refreshTable(session.id);
    } finally {
      setWorking(false);
    }
  };

  const act = async (action) => {
    if (!session?.id || !myTurn || working) return;

    setWorking(true);
    setMessage("");

    try {
      await submitBlackjackAction({
        sessionId: session.id,
        action,
        expectedVersion: session.version,
      });
      await refreshTable(session.id);
    } catch (err) {
      setMessage(
        String(err.message || "").includes("STALE_GAME_STATE")
          ? "The table changed. Refreshing…"
          : err.message || "Action was not accepted."
      );
      await refreshTable(session.id);
    } finally {
      setWorking(false);
    }
  };

  const sitOut = async () => {
    if (!session?.id || myState?.status !== "betting" || working) return;

    setWorking(true);
    setMessage("");

    try {
      await sitOutBlackjackHand({
        sessionId: session.id,
        expectedVersion: session.version,
      });
      await refreshTable(session.id);
    } catch (err) {
      setMessage(
        String(err.message || "").includes("STALE_GAME_STATE")
          ? "The table changed. Refreshing…"
          : err.message || "Could not sit out this hand."
      );
      await refreshTable(session.id);
    } finally {
      setWorking(false);
    }
  };

  const resumeBetting = async () => {
    if (!session?.id || state.phase !== "betting" || working) return;

    setWorking(true);
    setMessage("");

    try {
      await resumeBlackjackBetting({
        sessionId: session.id,
        expectedVersion: session.version,
      });
      await refreshTable(session.id);
    } catch (err) {
      setMessage(
        String(err.message || "").includes("STALE_GAME_STATE")
          ? "The table changed. Refreshing…"
          : err.message || "Could not rejoin betting."
      );
      await refreshTable(session.id);
    } finally {
      setWorking(false);
    }
  };

  const react = async (reactionKey) => {
    if (!session?.id || !myPlayer || reactionWorking) return;

    setReactionWorking(true);

    try {
      const id = await sendBlackjackReaction({
        sessionId: session.id,
        reactionKey,
      });

      showReaction({
        id,
        seat: myPlayer.seat,
        user_id: currentUser.id,
        reaction_key: reactionKey,
      });
    } catch (err) {
      if (!String(err.message || "").includes("REACTION_COOLDOWN")) {
        setMessage(err.message || "Reaction could not be sent.");
      }
    } finally {
      window.setTimeout(() => setReactionWorking(false), 450);
    }
  };

  const renderPlayerActions = (extraClass = "") => (
    <div className={`online-bj-actions ${extraClass}`.trim()}>
      <button
        className="online-bj-action is-hit"
        onClick={() => act("hit")}
        disabled={working}
      >
        <FaBolt /> Hit
      </button>

      <button
        className="online-bj-action"
        onClick={() => act("stand")}
        disabled={working}
      >
        <FaHandPaper /> Stand
      </button>

      <button
        className="online-bj-action is-double"
        onClick={() => act("double")}
        disabled={working || !canDouble}
        title={
          canDouble
            ? `Match ${money(myActiveHand?.bet)} and take exactly one card`
            : "Double requires two cards and enough chips to match the hand wager"
        }
      >
        <FaMoneyBillWave /> Double
      </button>

      <button
        className="online-bj-action is-split"
        onClick={() => act("split")}
        disabled={working || !canSplit}
        title={
          canSplit
            ? `Add ${money(myActiveHand?.bet)} for another hand`
            : "Split requires two equal-value cards, enough chips, and fewer than four hands"
        }
      >
        <FaLayerGroup /> Split
      </button>

      <button
        className="online-bj-action is-surrender"
        onClick={() => act("surrender")}
        disabled={working || !canSurrender}
        title={
          canSurrender
            ? "Give up the original hand and recover half your wager"
            : "Surrender is only available before hitting or splitting"
        }
      >
        <FaFlag /> Surrender
      </button>
    </div>
  );

  const leave = async () => {
    if (!session?.id || working) return;

    const active = session.status === "active";
    const prompt = active
      ? "Leave this blackjack table? Any wager currently in play will be forfeited and your seat will be released."
      : "Leave this blackjack table and release your seat?";

    if (!window.confirm(prompt)) return;

    setWorking(true);
    setMessage("");

    try {
      if (session.game_key === "blackjack") {
        await leaveBlackjackSession(session.id);
      } else {
        await leaveGameSession(session.id);
      }

      setSession(null);
      setPlayers([]);
      setBet(DEFAULT_BET);
      setSecondsLeft(null);
      setJoinCodeInput("");
      setSeatReactions({});
      setOnlineUserIds(new Set());
      setActivityFeed([]);
      seenReactionIdsRef.current = new Set();
      lastActivityMessageRef.current = "";
      setMessage("");
      navigate("/games");
    } catch (err) {
      setMessage(err.message || "Could not leave the table.");
    } finally {
      setWorking(false);
    }
  };

  const copyCode = async () => {
    if (!session?.join_code) return;

    try {
      await navigator.clipboard.writeText(session.join_code);
      setMessage("Invite code copied.");
    } catch {
      setMessage(`Invite code: ${session.join_code}`);
    }
  };

  if (!currentUser) {
    return (
      <div className="online-bj-page">
        <div className="online-bj-shell online-bj-login">
          <GameRoomBackButton standalone />
          <FaUsers />
          <h1>Online Blackjack</h1>
          <p>Sign in to Hobbli to join a live blackjack table.</p>
          <Link to="/login" className="online-bj-primary">
            Sign In
          </Link>
          <Link to="/games/blackjack" className="online-bj-secondary">
            Play Solo Blackjack
          </Link>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="online-bj-page">
        <div className="online-bj-shell">
          <div className="online-bj-topbar">
            <GameRoomBackButton standalone />
            <Link to="/games/blackjack">Solo Blackjack</Link>
          </div>

          <header className="online-bj-hero">
            <span>HOBBLI LIVE · BLACKJACK</span>
            <h1>Take a Seat</h1>
            <p>
              Play 1–4 players against the dealer with one shared six-deck
              shoe.
            </p>
          </header>

          <div className="online-bj-lobby-grid">
            <section>
              <FaGamepad />
              <h2>Create Private Table</h2>
              <p>Share a six-character code with friends.</p>
              <button
                className="online-bj-primary"
                onClick={() => createTable("invite")}
                disabled={working}
              >
                Create Private Table
              </button>
            </section>

            <section>
              <FaLink />
              <h2>Join Private Table</h2>
              <form onSubmit={joinPrivate}>
                <input
                  value={joinCodeInput}
                  onChange={(event) =>
                    setJoinCodeInput(event.target.value.toUpperCase())
                  }
                  maxLength={6}
                  placeholder="ABC123"
                />
                <button
                  className="online-bj-primary"
                  disabled={working || !joinCodeInput.trim()}
                >
                  Join Table
                </button>
              </form>
            </section>

            <section>
              <FaUsers />
              <h2>Public Table</h2>
              <p>
                Join the oldest open public table, or create one automatically.
              </p>
              <button
                className="online-bj-primary"
                onClick={joinPublic}
                disabled={working}
              >
                Join Public Table
              </button>
            </section>
          </div>

          {message ? <div className="online-bj-message">{message}</div> : null}
        </div>
      </div>
    );
  }

  const waiting = session.status === "waiting";
  const allReady =
    activePlayers.length > 0 && activePlayers.every((player) => player.ready);
  const seats = [1, 2, 3, 4].map((seat) =>
    activePlayers.find((player) => player.seat === seat)
  );
  const dealerCards = state.dealer_cards || [];

  const phaseLabel = (() => {
    if (state.phase === "betting") return "Betting open";
    if (state.phase === "insurance") return "Insurance decision";
    if (state.phase === "player") {
      return myTurn ? "Your turn" : `Seat ${state.turn_seat} to act`;
    }
    if (state.phase === "complete") return "Round complete";
    if (state.phase === "dealt") return "Dealing";
    return "Table active";
  })();

  return (
    <div
      className={`online-bj-page phase-${state.phase || "waiting"} ${
        myTurn ? "is-my-turn" : ""
      }`.trim()}
    >
      <div className="online-bj-shell">
        <div className="online-bj-topbar">
          <GameRoomBackButton standalone />
          <div className="online-bj-topbar-actions">
            <Link to="/games/blackjack">Solo Blackjack</Link>
            {session ? (
              <button
                type="button"
                className="online-bj-leave-table"
                onClick={leave}
                disabled={working}
              >
                <FaSignOutAlt /> Leave Table
              </button>
            ) : null}
          </div>
        </div>

        <header className="online-bj-table-header">
          <div>
            <span>LIVE TABLE · ROUND {state.round_number || 0}</span>
            <h1>Online Blackjack</h1>
            <p>{state.message || (waiting ? "Waiting for players." : "Table active.")}</p>
          </div>
          <button className="online-bj-code" onClick={copyCode}>
            <span>INVITE CODE</span>
            <strong>{session.join_code}</strong>
            <FaCopy />
          </button>
        </header>

        {waiting ? (
          <>
            <div className="online-bj-seats-lobby">
              {seats.map((player, index) => (
                <div
                  key={index}
                  className={`online-bj-seat-card ${
                    player?.user_id === currentUser.id ? "is-you" : ""
                  }`}
                >
                  <span>SEAT {index + 1}</span>
                  <strong>{player?.display_name || "OPEN SEAT"}</strong>
                  <small>
                    {player
                      ? `${player.ready ? "READY" : "NOT READY"}${
                          player.user_id === session.host_user_id ? " · HOST" : ""
                        }`
                      : "WAITING"}
                  </small>
                </div>
              ))}
            </div>

            <section className="online-bj-ready-panel">
              <div>
                <h2>
                  {activePlayers.length === 1
                    ? "Waiting at the table"
                    : "Ready to deal?"}
                </h2>
                <p>
                  Every seated player presses Ready. The host starts whenever
                  the table is set — 1 to 4 players.
                </p>
              </div>

              <div className="online-bj-ready-actions">
                <button
                  className="online-bj-primary"
                  onClick={toggleReady}
                  disabled={working}
                >
                  <FaCheck /> {myPlayer?.ready ? "Not Ready" : "I'm Ready"}
                </button>

                {host && (
                  <button
                    className="online-bj-primary"
                    onClick={startTable}
                    disabled={working || !allReady}
                  >
                    <FaPlay /> Start Table
                  </button>
                )}

                <button
                  className="online-bj-secondary"
                  onClick={leave}
                  disabled={working}
                >
                  <FaSignOutAlt /> Leave
                </button>
              </div>
            </section>
          </>
        ) : (
          <div className="online-bj-game-layout">
            <main className="online-bj-table">
              <section className="online-bj-dealer">
                <div className="online-bj-zone-heading">
                  <div>
                    <span>HOUSE</span>
                    <h2>Dealer</h2>
                  </div>
                  {dealerCards.length ? (
                    <strong className="online-bj-dealer-status">
                      {state.dealer_total != null
                        ? `Dealer has ${state.dealer_total}`
                        : `Dealer showing ${cardTotal(dealerCards)}`}
                    </strong>
                  ) : null}
                </div>

                <div className="online-bj-cards">
                  {dealerCards.map((card) => (
                    <PlayingCard key={card.id} card={card} />
                  ))}
                  {["player", "insurance"].includes(state.phase) &&
                  dealerCards.length === 1 ? (
                    <PlayingCard hidden />
                  ) : null}
                </div>
              </section>

              <div className="online-bj-table-center">
                {secondsLeft != null && (
                  <div
                    className={`online-bj-timer ${
                      secondsLeft <= 5 ? "is-low" : ""
                    }`}
                  >
                    {secondsLeft}s
                  </div>
                )}
                <div className="online-bj-turn-label">{phaseLabel}</div>
              </div>

              <section className="online-bj-player-arc">
                {[1, 2, 3, 4].map((seat) => {
                  const player = activePlayers.find(
                    (item) => item.seat === seat
                  );
                  const playerState = statePlayers[String(seat)];

                  if (!player && !playerState) {
                    return (
                      <div
                        className={`online-bj-player-seat seat-${seat} is-open`}
                        key={seat}
                      >
                        <span>Seat {seat}</span>
                        <strong>Open</strong>
                      </div>
                    );
                  }

                  const hands = playerHands(playerState);
                  const active =
                    state.phase === "player" && state.turn_seat === seat;
                  const mine = player?.user_id === currentUser.id;
                  const totalBet = hands.reduce(
                    (sum, hand) => sum + Number(hand.bet || 0),
                    0
                  );

                  return (
                    <article
                      className={`online-bj-player-seat seat-${seat} ${
                        active ? "is-active" : ""
                      } ${mine ? "is-you" : ""} ${
                        ["sitting_out", "auto_sit_out"].includes(playerState?.status)
                          ? "is-sitting-out"
                          : ""
                      }`}
                      key={seat}
                    >
                      {seatReactions[seat] ? (
                        <div
                          className="online-bj-reaction-bubble"
                          role="status"
                          aria-live="polite"
                        >
                          <span>{seatReactions[seat].emoji}</span>
                          <strong>{seatReactions[seat].label}</strong>
                        </div>
                      ) : null}

                      <div className="online-bj-player-seat__head">
                        <div>
                          <span>SEAT {seat}</span>
                          <strong>
                            {player?.display_name || "Player"}
                            {mine ? " · YOU" : ""}
                          </strong>
                        </div>
                        <div className="online-bj-seat-head-right">
                          <div className="online-bj-seat-tags">
                            {player?.user_id === session.host_user_id ? (
                              <em>HOST</em>
                            ) : null}
                            {player ? (
                              <em
                                className={`online-bj-presence ${
                                  onlineUserIds.has(player.user_id)
                                    ? "is-online"
                                    : "is-away"
                                }`}
                                title={
                                  onlineUserIds.has(player.user_id)
                                    ? "Currently at this live table"
                                    : "Not currently connected to this live table"
                                }
                              >
                                <i />
                                {onlineUserIds.has(player.user_id)
                                  ? "AT TABLE"
                                  : "AWAY"}
                              </em>
                            ) : null}
                          </div>
                          <b>
                            {player && !playerState && session.status === "active"
                              ? "WAITING NEXT HAND"
                              : statusLabel(playerState?.status)}
                          </b>
                        </div>
                      </div>

                      {hands.length ? (
                        <div
                          className={`online-bj-hand-stack ${
                            hands.length > 1 ? "has-splits" : ""
                          }`}
                        >
                          {hands.map((hand, handIndex) => {
                            const handActive =
                              active &&
                              Number(playerState?.active_hand || 0) === handIndex;
                            const total = cardTotal(hand.cards || []);

                            return (
                              <div
                                className={`online-bj-hand ${
                                  handActive ? "is-active-hand" : ""
                                }`}
                                key={`${seat}-hand-${handIndex}`}
                              >
                                <div className="online-bj-hand__title">
                                  <span>
                                    {hands.length > 1
                                      ? `HAND ${handIndex + 1}`
                                      : "HAND"}
                                  </span>
                                  <strong>{total}</strong>
                                </div>

                                <div className="online-bj-cards is-player">
                                  {(hand.cards || []).map((card) => (
                                    <PlayingCard
                                      key={card.id}
                                      card={card}
                                      compact={!mine || hands.length > 1}
                                    />
                                  ))}
                                </div>

                                <div className="online-bj-hand__meta">
                                  <span>Bet {money(hand.bet)}</span>
                                  {hand.doubled ? <span>DOUBLED</span> : null}
                                  {hand.from_split ? <span>SPLIT</span> : null}
                                  {hand.split_aces ? <span>SPLIT ACES · AUTO-STAND</span> : null}
                                  {hand.result ? (
                                    <span className={`result-${hand.result}`}>
                                      {hand.result.toUpperCase()}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="online-bj-empty-hand">
                          {player && !playerState && session.status === "active"
                            ? "Joined table · waiting for next hand"
                            : playerState?.status === "auto_sit_out"
                            ? Number(playerState?.missed_betting || 0) >= 2
                              ? "Inactive · tap I'm Back during betting to return"
                              : "Sitting out · tap I'm Back during betting to return"
                            : playerState?.status === "sitting_out"
                            ? "Sitting out this hand"
                            : playerState?.status === "out_of_chips"
                            ? "No chips available"
                            : state.phase === "betting"
                            ? "Waiting for wager"
                            : "No hand this round"}
                        </div>
                      )}

                      <div className="online-bj-seat-meta">
                        <span>{money(playerState?.bankroll)}</span>
                        {totalBet > 0 ? <span>In play {money(totalBet)}</span> : null}
                        {playerState?.insurance_bet > 0 ? (
                          <span>Insurance {money(playerState.insurance_bet)}</span>
                        ) : null}
                        {playerState?.result &&
                        playerState.result !== "mixed" ? (
                          <span className={`result-${playerState.result}`}>
                            {playerState.result.toUpperCase()}
                          </span>
                        ) : null}
                      </div>

                      {mine && state.phase === "player" && myTurn ? (
                        <div className="online-bj-mobile-turn-controls">
                          <span className="online-bj-mobile-turn-controls__label">
                            YOUR MOVE · {secondsLeft != null ? `${secondsLeft}s` : "ACT NOW"}
                          </span>
                          {renderPlayerActions("online-bj-actions--mobile")}
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </section>

              {state.phase === "betting" && myState?.status === "betting" ? (
                <div className="online-bj-controls-panel">
                  <div>
                    <span>YOUR BANKROLL</span>
                    <strong>{money(myState.bankroll)}</strong>
                  </div>

                  <div className="online-bj-bet-tools">
                    <div className="online-bj-bet-picker">
                      <button
                        type="button"
                        onClick={() =>
                          setBet((value) => Math.max(25, value - BET_STEP))
                        }
                      >
                        −25
                      </button>
                      <strong>{money(Math.min(bet, myMaxWager))}</strong>
                      <button
                        type="button"
                        onClick={() =>
                          setBet((value) =>
                            Math.min(myMaxWager, value + BET_STEP)
                          )
                        }
                        disabled={bet >= myMaxWager}
                      >
                        +25
                      </button>
                    </div>

                    <div className="online-bj-quick-bets" aria-label="Quick wager">
                      {[25, 50, 100, 250].map((amount) => (
                        <button
                          type="button"
                          key={amount}
                          onClick={() => setBet(Math.min(amount, myMaxWager))}
                          disabled={myMaxWager < amount}
                        >
                          ${amount}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setBet(myMaxWager)}
                        disabled={myMaxWager < 25}
                      >
                        Max
                      </button>
                    </div>
                  </div>

                  <div className="online-bj-bet-actions">
                    <button
                      className="online-bj-primary"
                      onClick={placeBet}
                      disabled={working || myMaxWager < 25}
                    >
                      Place Bet
                    </button>
                    <button
                      type="button"
                      className="online-bj-secondary"
                      onClick={sitOut}
                      disabled={working}
                    >
                      Sit Out
                    </button>
                  </div>
                </div>
              ) : null}

              {state.phase === "betting" && myState?.refilled ? (
                <div className="online-bj-waiting-callout is-refill">
                  Play chips refreshed to $1,000. {Number(myState?.refills_remaining ?? 0)} free refresh{Number(myState?.refills_remaining ?? 0) === 1 ? "" : "es"} remaining in this 24-hour window.
                </div>
              ) : null}

              {state.phase === "betting" && myState?.status === "auto_sit_out" ? (
                <div className="online-bj-auto-sitout-callout">
                  <div>
                    <strong>You’re sitting out</strong>
                    <span>
                      {Number(myState?.missed_betting || 0) >= 2
                        ? "Two betting windows expired without a wager. Your seat is safe and you’ll remain out until you return."
                        : "You chose to sit out. Your seat is safe and you’ll remain out until you return."}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="online-bj-primary"
                    onClick={resumeBetting}
                    disabled={working || secondsLeft === 0}
                  >
                    I’m Back · Rejoin Betting
                  </button>
                </div>
              ) : null}

              {state.phase === "betting" && myState?.status === "bet" ? (
                <div className="online-bj-waiting-callout is-bet-locked">
                  Your bet is locked. The hand deals as soon as everyone bets,
                  or when the clock reaches zero.
                </div>
              ) : null}

              {state.phase === "betting" &&
              ["sitting_out", "out_of_chips"].includes(myState?.status) ? (
                <div className="online-bj-waiting-callout">
                  {myState?.status === "out_of_chips" ? (
                    <>
                      <strong>You’re out of playable chips.</strong>{" "}
                      {Number(myState?.refills_remaining || 0) > 0
                        ? "A free $1,000 refresh will be applied automatically at the next betting window."
                        : `Both free refreshes have been used. Next refresh in ${
                            formatRefillCountdown(myState?.next_refill_at) || "under a minute"
                          }.`}
                    </>
                  ) : (
                    "You are sitting out. Tap I’m Back during betting when you want to return."
                  )}
                </div>
              ) : null}

              {state.phase === "insurance" &&
              myState?.insurance_status === "pending" ? (
                <div className="online-bj-insurance-panel">
                  <div>
                    <span>INSURANCE OFFERED</span>
                    <h3>Dealer is showing an Ace</h3>
                    <p>
                      Insurance costs {money(myState.insurance_max)} and pays
                      2:1 if the dealer has blackjack.
                    </p>
                  </div>
                  <div className="online-bj-insurance-actions">
                    <button
                      className="online-bj-secondary"
                      onClick={() => insuranceDecision(false)}
                      disabled={working}
                    >
                      No Insurance
                    </button>
                    <button
                      className="online-bj-primary"
                      onClick={() => insuranceDecision(true)}
                      disabled={
                        working ||
                        Number(myState.bankroll || 0) <
                          Number(myState.insurance_max || 0)
                      }
                    >
                      <FaShieldAlt /> Take {money(myState.insurance_max)}
                    </button>
                  </div>
                </div>
              ) : null}

              {state.phase === "insurance" &&
              myState?.insurance_status &&
              myState.insurance_status !== "pending" ? (
                <div className="online-bj-waiting-callout">
                  Insurance decision locked: {myState.insurance_status}.
                  Waiting for the other seats.
                </div>
              ) : null}

              {state.phase === "player" && myTurn
                ? renderPlayerActions("online-bj-actions--desktop")
                : null}

              {session.status === "active" && myPlayer ? (
                <section className="online-bj-reactions" aria-label="Table reactions">
                  <div className="online-bj-reactions__label">
                    <span>TABLE REACTIONS</span>
                    <small>Friendly presets · visible for a few seconds</small>
                  </div>
                  <div className="online-bj-reaction-buttons">
                    {REACTIONS.map((reaction) => (
                      <button
                        type="button"
                        key={reaction.key}
                        onClick={() => react(reaction.key)}
                        disabled={reactionWorking}
                        title={reaction.label}
                        aria-label={reaction.label}
                      >
                        <span>{reaction.emoji}</span>
                        <strong>{reaction.label}</strong>
                      </button>
                    ))}
                  </div>
                </section>
              ) : null}

              {state.phase === "complete" && myState && myRoundStake > 0 ? (
                <div
                  className={`online-bj-round-summary ${
                    myRoundNet > 0
                      ? "is-win"
                      : myRoundNet < 0
                      ? "is-loss"
                      : "is-push"
                  }`}
                >
                  <span>YOUR ROUND</span>
                  <strong>
                    {myRoundNet > 0 ? "+" : ""}
                    {money(myRoundNet)}
                  </strong>
                  <small>
                    {myRoundNet > 0
                      ? "Nice hand."
                      : myRoundNet < 0
                      ? "Next hand is coming up."
                      : "Push for the round."}
                  </small>
                </div>
              ) : null}

              {state.phase === "complete" ? (
                <div className="online-bj-auto-next">
                  <strong>Next hand starts automatically.</strong>
                  <span>
                    Betting opens {secondsLeft != null ? `in ${secondsLeft}s` : "shortly"}.
                  </span>
                </div>
              ) : null}
            </main>

            <aside className="online-bj-side">
              <section>
                <span>TABLE</span>
                <h3>
                  {session.visibility === "public"
                    ? "Public Table"
                    : "Private Table"}
                </h3>
                <p>
                  {activePlayers.length} of {session.max_players} seats occupied
                </p>
              </section>

              <section>
                <span>TABLE FLOW</span>
                <p>
                  20s to bet · inactive seats sit out<br />
                  Sit Out stays active until you return<br />
                  25s player turns · timeout stands<br />
                  Results show ~5s, then betting reopens
                </p>
              </section>

              <section>
                <span>RULES</span>
                <p>
                  6-deck shared shoe<br />
                  Dealer stands on soft 17<br />
                  Blackjack pays 3:2<br />
                  Split to 4 hands · double after split<br />
                  Split Aces receive one card each
                </p>
              </section>

              <section className="online-bj-activity-panel">
                <span>LIVE ACTIVITY</span>
                <div className="online-bj-activity-list">
                  {activityFeed.length ? (
                    activityFeed.map((entry) => (
                      <div key={entry.id}>
                        <strong>{entry.text}</strong>
                        <small>{entry.time}</small>
                      </div>
                    ))
                  ) : (
                    <p>Table events will appear here as the hand moves.</p>
                  )}
                </div>
              </section>
            </aside>
          </div>
        )}

        {message ? <div className="online-bj-message">{message}</div> : null}
      </div>
    </div>
  );
}
